import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Button,
  Input,
  Spinner,
  Chip,
  Tooltip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { MdEdit, MdHistory } from 'react-icons/md';
import { BsPlusCircle } from 'react-icons/bs';
import toast from 'react-hot-toast';

const PROXY = process.env.REACT_APP_API_URL + 'api/';

const columns = [
  { uid: 'no', name: 'No' },
  { uid: 'oem', name: 'OEM' },
  { uid: 'pace', name: 'Pace (leads/min)' },
  { uid: 'flag', name: 'Status' },
  { uid: 'updatedAt', name: 'Last Updated' },
  { uid: 'actions', name: 'Actions' },
];

const OEM_OPTIONS = ['OLA', 'TVS', 'AMPERE', 'SIMPLE'];

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const LeadPacingPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit modal
  const [editingConfig, setEditingConfig] = useState(null);
  const [newPace, setNewPace] = useState('');
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  // Add modal
  const [addOem, setAddOem] = useState('');
  const [addPace, setAddPace] = useState('7');
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();

  // Logs modal
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsOem, setLogsOem] = useState('');
  const { isOpen: isLogsOpen, onOpen: onLogsOpen, onClose: onLogsClose } = useDisclosure();

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${PROXY}lead-pacing`);
      setConfigs(res.data.result || []);
    } catch (err) {
      console.error('Error fetching pacing configs:', err);
      toast.error('Failed to fetch pacing configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Add
  const handleAdd = async () => {
    const pace = Number(addPace);
    if (pace < 0 || pace > 50 || !Number.isInteger(pace)) {
      toast.error('Pace must be between 0 and 50');
      return;
    }
    if (!addOem) {
      toast.error('Please select an OEM');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${PROXY}lead-pacing`, { oem: addOem, pace });
      toast.success(res.data.message || 'Pacing config added!');
      onAddClose();
      setAddOem('');
      setAddPace('7');
      fetchConfigs();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  // Edit
  const openEdit = (config) => {
    setEditingConfig(config);
    setNewPace(config.pace.toString());
    onEditOpen();
  };

  const handleUpdate = async () => {
    const pace = Number(newPace);
    if (pace < 0 || pace > 50 || !Number.isInteger(pace)) {
      toast.error('Pace must be between 0 and 50');
      return;
    }
    if (!editingConfig?._id) return;
    setSaving(true);
    try {
      const res = await axios.put(`${PROXY}lead-pacing/${editingConfig._id}`, { pace });
      toast.success(res.data.message || 'Pacing updated!');
      onEditClose();
      setEditingConfig(null);
      fetchConfigs();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Logs
  const openLogs = async (oem) => {
    setLogsOem(oem);
    setLogsLoading(true);
    onLogsOpen();
    try {
      const res = await axios.get(`${PROXY}lead-pacing/logs?oem=${oem}&limit=50`);
      setLogs(res.data.result || []);
    } catch (err) {
      toast.error('Failed to fetch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Existing OEMs (to disable in Add dropdown)
  const existingOems = configs.map((c) => c.oem);
  const availableOems = OEM_OPTIONS.filter((o) => !existingOems.includes(o));

  // Render cell
  const renderCell = (row, columnKey, index) => {
    switch (columnKey) {
      case 'no':
        return <p className="text-black text-medium font-bold">{index + 1}</p>;
      case 'oem':
        return (
          <Chip color="primary" variant="flat" size="sm" className="font-bold">
            {row.oem}
          </Chip>
        );
      case 'pace':
        return (
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-default-800">{row.pace}</span>
            <span className="text-xs text-default-400">leads/min</span>
            {row.pace === 0 && (
              <Chip size="sm" color="warning" variant="flat">Paused</Chip>
            )}
          </div>
        );
      case 'flag':
        return (
          <Chip size="sm" color={row.flag === 1 ? 'success' : 'danger'} variant="flat">
            {row.flag === 1 ? 'Active' : 'Inactive'}
          </Chip>
        );
      case 'updatedAt':
        return <span className="text-sm text-default-500">{formatDate(row.updatedAt)}</span>;
      case 'actions':
        return (
          <div className="flex items-center gap-2">
            <Tooltip content="Edit Pace">
              <Button isIconOnly size="sm" variant="light" onPress={() => openEdit(row)}>
                <MdEdit size={18} className="text-default-600" />
              </Button>
            </Tooltip>
            <Tooltip content="View Change History">
              <Button isIconOnly size="sm" variant="light" onPress={() => openLogs(row.oem)}>
                <MdHistory size={18} className="text-default-600" />
              </Button>
            </Tooltip>
          </div>
        );
      default:
        return row[columnKey] || '-';
    }
  };

  return (
    <div className="inline-flex overflow-hidden max-w-full w-full h-full rounded-global shadow-global">
      <div className="bg-white rounded-global shadow-global transition-width duration-300 h-full flex-[100] max-w-full w-full">
        <div className="grid-layout h-full">
          {/* Header */}
          <div className="w-full gap-4 lead-view-header flex flex-wrap items-center justify-between border-b border-default-200 py-4 px-6">
            <div className="flex items-center 2xl:gap-5 gap-3 w-full justify-between">
              <div className="total-leads-documents flex items-center whitespace-nowrap gap-1">
                <span className="text-medium text-success">Lead Pacing Configurations</span>
                <span className="text-large font-bold">({configs.length})</span>
              </div>
              {availableOems.length > 0 && (
                <Button
                  variant="flat"
                  onClick={() => {
                    setAddOem('');
                    setAddPace('7');
                    onAddOpen();
                  }}
                  className="bg-success text-white"
                  startContent={<BsPlusCircle size={18} />}
                >
                  Add OEM Pacing
                </Button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="leadsData overflow-x-auto h-[calc(100%-72px)] flex flex-col">
            {loading ? (
              <div className="w-full h-full flex items-center justify-center gap-2">
                <Spinner color="success" />
                <p className="!text-success text-3xl">Loading...</p>
              </div>
            ) : configs.length === 0 ? (
              <div className="text-center h-full flex flex-col items-center justify-center gap-3">
                <p className="text-3xl font-bold text-default-300">No pacing configs found</p>
                <p className="text-default-400">Click "Add OEM Pacing" to create one</p>
              </div>
            ) : (
              <div className="shadow-none px-4 py-6 rounded-outSideglobal w-full overflow-x-auto default-scrollbar h-full overflow-hidden flex flex-col">
                <div className="overflow-x-auto overflow-y-auto h-full rounded-outSideglobal shadow-global default-scrollbar !bg-[rgb(229_243_251_/_var(--tw-bg-opacity,_1))] p-5 !pt-7">
                  <Table
                    aria-label="Lead Pacing table"
                    removeWrapper
                    classNames={{
                      th: 'bg-transparent text-default-600 font-semibold text-sm border-none',
                      td: 'border-none !py-[12px] !h-[58px]',
                      tr: 'border-none',
                    }}
                  >
                    <TableHeader columns={columns}>
                      {(column) => (
                        <TableColumn key={column.uid} align={column.uid === 'actions' ? 'center' : 'start'}>
                          {column.name}
                        </TableColumn>
                      )}
                    </TableHeader>
                    <TableBody items={configs}>
                      {(item) => (
                        <TableRow key={item._id}>
                          {(columnKey) => (
                            <TableCell>
                              {renderCell(item, columnKey, configs.indexOf(item))}
                            </TableCell>
                          )}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ Add Modal ═══ */}
      <Modal
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        isOpen={isAddOpen}
        onOpenChange={(open) => { if (!open) onAddClose(); }}
        size="sm"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Add OEM Pacing</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-default-700">OEM</label>
                    <div className="flex gap-2">
                      {availableOems.map((oem) => (
                        <Button
                          key={oem}
                          size="sm"
                          variant={addOem === oem ? 'solid' : 'bordered'}
                          color={addOem === oem ? 'success' : 'default'}
                          className={addOem === oem ? 'text-white' : ''}
                          onPress={() => setAddOem(oem)}
                        >
                          {oem}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Input
                    type="number"
                    label="Leads per minute"
                    labelPlacement="outside"
                    placeholder="Enter pace (0–50)"
                    value={addPace}
                    onChange={(e) => setAddPace(e.target.value)}
                    min={0}
                    max={50}
                    isRequired
                    color="success"
                    variant="bordered"
                    description="Maximum: 50 leads per minute (0 = paused)"
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>Cancel</Button>
                <Button
                  color="success"
                  className="text-white"
                  onPress={handleAdd}
                  isLoading={saving}
                  isDisabled={!addOem || addPace === '' || Number(addPace) < 0 || Number(addPace) > 50}
                >
                  Save
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* ═══ Edit Modal ═══ */}
      <Modal
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        isOpen={isEditOpen}
        onOpenChange={(open) => { if (!open) { onEditClose(); setEditingConfig(null); } }}
        size="sm"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Edit {editingConfig?.oem} Lead Pace</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <Input
                    label="OEM"
                    value={editingConfig?.oem || ''}
                    isReadOnly
                    variant="bordered"
                    color="success"
                    labelPlacement="outside"
                  />
                  <Input
                    type="number"
                    label="Leads per minute"
                    labelPlacement="outside"
                    placeholder="Enter pace (0–50)"
                    value={newPace}
                    onChange={(e) => setNewPace(e.target.value)}
                    min={0}
                    max={50}
                    isRequired
                    color="success"
                    variant="bordered"
                    description="Maximum: 50 leads per minute (0 = paused)"
                  />
                  <p className="text-xs text-default-400">
                    Current pace: <strong>{editingConfig?.pace}</strong> leads/min
                  </p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>Cancel</Button>
                <Button
                  color="success"
                  className="text-white"
                  onPress={handleUpdate}
                  isLoading={saving}
                  isDisabled={newPace === '' || Number(newPace) < 0 || Number(newPace) > 50}
                >
                  Update
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* ═══ Logs Modal ═══ */}
      <Modal
        isDismissable={true}
        isOpen={isLogsOpen}
        onOpenChange={(open) => { if (!open) onLogsClose(); }}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{logsOem} Pace Change History</ModalHeader>
              <ModalBody>
                {logsLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <Spinner color="success" />
                  </div>
                ) : logs.length === 0 ? (
                  <p className="text-center text-default-400 py-8">No change logs found.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {logs.map((log, i) => (
                      <div
                        key={log._id || i}
                        className="flex items-center justify-between bg-default-50 rounded-xl px-4 py-3 border border-default-100"
                      >
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-default-700">
                              {log.adminId?.name || 'Unknown'}
                            </span>
                            <span className="text-xs text-default-400">
                              ({log.adminId?.email})
                            </span>
                          </div>
                          <span className="text-xs text-default-400">
                            {formatDate(log.createdAt)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Chip size="sm" color="danger" variant="flat">{log.oldPace}</Chip>
                          <span className="text-default-400">→</span>
                          <Chip size="sm" color="success" variant="flat">{log.newPace}</Chip>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>Close</Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default LeadPacingPage;
